import { Skeleton } from '@doccareplus/ui';

/**
 * Loading placeholders for the records feature. A `role="status"` region
 * (with an sr-only label) wrapping `aria-hidden` shimmer primitives, so
 * assistive tech announces "loading" once instead of reading every block.
 * Mirrors the real prescription-row shape (icon + name/date/meds line) at
 * both mobile and desktop widths — it's a single responsive component, not
 * a separate mobile/desktop split.
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A single prescription row: icon square, provider/date/medicines lines, chevron. */
function PrescriptionRowSkeleton() {
  return (
    <div
      className="flex w-full items-center gap-3 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <Skeleton className="h-9 w-9 shrink-0 rounded-[10px]" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-1/4" />
        <Skeleton className="h-3 w-3/5" />
      </div>
      <Skeleton className="h-4 w-4 shrink-0" />
    </div>
  );
}

/** The prescriptions list — a stacked series of prescription-row placeholders. */
export function PrescriptionsListSkeleton({ label, rows = 4 }: { label: string; rows?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, i) => (
          <PrescriptionRowSkeleton key={i} />
        ))}
      </div>
    </LoadingRegion>
  );
}
